import React, { useState } from 'react';
import { 
  Search, 
  MapPin, 
  ChevronDown,
  Globe,
  TrendingUp,
  CheckCircle,
  DollarSign,
  Users,
  Target,
  X,
  Zap,
  BarChart3,
  Eye,
  Activity,
  ArrowUpNarrowWide,
  Plus,
  AlertCircle
} from 'lucide-react';

// Get API URL from environment variable
const API_URL = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

const KeywordExplorer = () => {
  const [keyword, setKeyword] = useState('');
  const [country, setCountry] = useState('United States');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showSearchSection, setShowSearchSection] = useState(true);
  const [error, setError] = useState('');
  const [apiData, setApiData] = useState(null);

  const countries = [
    { code: 'us', name: 'United States', displayCode: 'US' },
    { code: 'uk', name: 'United Kingdom', displayCode: 'GB' },
    { code: 'ca', name: 'Canada', displayCode: 'CA' },
    { code: 'au', name: 'Australia', displayCode: 'AU' },
    { code: 'de', name: 'Germany', displayCode: 'DE' },
    { code: 'fr', name: 'France', displayCode: 'FR' },
    { code: 'es', name: 'Spain', displayCode: 'ES' },
    { code: 'it', name: 'Italy', displayCode: 'IT' },
    { code: 'nl', name: 'Netherlands', displayCode: 'NL' },
    { code: 'se', name: 'Sweden', displayCode: 'SE' },
    { code: 'ru', name: 'Russia', displayCode: 'RU' },
    { code: 'br', name: 'Brazil', displayCode: 'BR' },
    { code: 'ar', name: 'Argentina', displayCode: 'AR' },
    { code: 'be', name: 'Belgium', displayCode: 'BE' },
    { code: 'ch', name: 'Switzerland', displayCode: 'CH' },
    { code: 'dk', name: 'Denmark', displayCode: 'DK' },
    { code: 'fi', name: 'Finland', displayCode: 'FI' },
    { code: 'hk', name: 'Hong Kong', displayCode: 'HK' },
    { code: 'ie', name: 'Ireland', displayCode: 'IE' },
    { code: 'il', name: 'Israel', displayCode: 'IL' },
    { code: 'mx', name: 'Mexico', displayCode: 'MX' },
    { code: 'no', name: 'Norway', displayCode: 'NO' },
    { code: 'pl', name: 'Poland', displayCode: 'PL' },
    { code: 'sg', name: 'Singapore', displayCode: 'SG' },
    { code: 'tr', name: 'Turkey', displayCode: 'TR' },
    { code: 'jp', name: 'Japan', displayCode: 'JP' },
    { code: 'in', name: 'India', displayCode: 'IN' }
  ];

  // Safe number formatting utilities
  const safeNumber = (value, defaultValue = 0) => {
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  };

  const formatCurrency = (value) => {
    const num = safeNumber(value);
    return num > 0 ? `$${num.toFixed(2)}` : 'N/A';
  };

  const formatVolume = (value) => {
    const num = safeNumber(value);
    return num > 0 ? num.toLocaleString() : 'N/A';
  };

  const formatPercentage = (value) => {
    const num = safeNumber(value);
    return num >= 0 ? `${(num * 100).toFixed(0)}%` : 'N/A';
  };

  // Flag component
  const FlagImage = ({ countryCode, size = 16, className = "" }) => {
    return (
      <img 
        src={`https://flagsapi.com/${countryCode}/flat/${size}.png`}
        alt={`${countryCode} flag`}
        className={`rounded-sm ${className}`}
        style={{ width: `${size}px`, height: `${size * 0.75}px` }}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    );
  };

  // Error Alert Component
  const ErrorAlert = ({ message, onClose }) => {
    if (!message) return null;
    
    return (
      <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
        <span className="text-red-700 text-sm flex-1">{message}</span>
        <button
          onClick={onClose}
          className="text-red-500 hover:text-red-700 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  };

  // API function to call keyword endpoint - UPDATED with environment variable
  const callKeywordAPI = async (keyword, country) => {
    try {
      const selectedCountry = countries.find(c => c.name === country);
      
      // Use the API_URL from environment variable
      const response = await fetch(`${API_URL}/api/keyword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          keyword: keyword.trim(),
          country: selectedCountry?.code || 'us',
          countryName: country
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API Error: ${response.status}`);
      }

      const data = await response.json();
// Debug log
      return data;
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  };

  // Updated difficulty calculation function
  const calculateDifficulty = (competition) => {
    if (!competition && competition !== 0) return { score: 0, level: 'N/A' };
    
    // New calculation: (competition * 100) + 5
    const difficultyScore = (parseFloat(competition) * 100) + 5;
    
    let level;
    if (difficultyScore >= 75) level = 'Hard';
    else if (difficultyScore >= 40) level = 'Medium';
    else level = 'Easy';
    
    return { score: Math.round(difficultyScore), level };
  };

  // Helper function to format trends data
  const formatTrends = (trends) => {
    if (!trends || trends === '' || trends === '-') return 'N/A';
    
    // If trends is a comma-separated string of numbers, calculate average and show as trend
    if (typeof trends === 'string' && trends.includes(',')) {
      const trendArray = trends.split(',').map(t => parseFloat(t.trim())).filter(t => !isNaN(t));
      if (trendArray.length > 0) {
        const avg = trendArray.reduce((a, b) => a + b, 0) / trendArray.length;
        const percentage = (avg * 100).toFixed(0);
        return `${percentage}%`;
      }
    }
    
    return 'N/A';
  };

  // Updated SERP features formatting function
  const formatSerpFeatures = (features) => {
    if (!features || features === '' || features === '-') return [];
    
    // Enhanced mapping of SERP feature codes to readable names
    const featureMap = {
      '1': 'Ads Top',
      '2': 'Ads Bottom', 
      '3': 'Videos',
      '4': 'Knowledge Panel',
      '5': 'Images',
      '6': 'Videos',
      '7': 'Shopping',
      '8': 'Local Pack',
      '9': 'Shopping',
      '10': 'People Also Ask',
      '11': 'Reviews',
      '12': 'Site Links',
      '13': 'Featured Snippet',
      '14': 'News',
      '15': 'Local Pack',
      '16': 'App Pack',
      '17': 'Flights',
      '18': 'Hotels',
      '19': 'Jobs',
      '20': 'Events',
      '21': 'Recipes',
      '22': 'Academic',
      '23': 'Finance',
      '24': 'Weather',
      '25': 'Sports',
      '26': 'Calculator',
      '27': 'Translation',
      '28': 'Currency',
      '29': 'Time Zone',
      '30': 'Unit Converter',
      '31': 'Dictionary',
      '32': 'Lyrics',
      '33': 'TV Listings',
      '34': 'Stocks',
      '35': 'Top Stories',
      '36': 'Featured Snippet',
      '37': 'Image Pack',
      '38': 'Twitter',
      '39': 'Related Questions',
      '40': 'Carousel',
      '41': 'Explore Panel',
      '42': 'Commercial',
      '43': 'Featured Snippet'
    };
    
    return features.split(',').map(code => {
      const trimmedCode = code.trim();
      return featureMap[trimmedCode] || `Feature ${trimmedCode}`;
    });
  };

  // Helper function to get difficulty color
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-600';
      case 'Medium': return 'text-yellow-600';
      case 'Hard': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    // Clear previous error
    setError('');
    
    // Validation: Check if keyword is empty
    if (!keyword.trim()) {
      setError('Please enter a keyword to search.');
      return;
    }

    // Additional validation: minimum length
    if (keyword.trim().length < 2) {
      setError('Keyword must be at least 2 characters long.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Call the API
      const data = await callKeywordAPI(keyword, country);
      setApiData(data);
      setShowResults(true);
      setShowSearchSection(false);
    } catch (error) {
      console.error('Error calling keyword API:', error);
      
      // Set appropriate error message
      if (error.message.includes('Failed to fetch')) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else if (error.message.includes('API Error: 404')) {
        setError('API endpoint not found. Please contact support.');
      } else if (error.message.includes('API Error: 500')) {
        setError('Server error occurred. Please try again later.');
      } else if (error.message.includes('API Error: 401')) {
        setError('Authentication failed. Please log in and try again.');
      } else {
        setError(error.message || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSearchSection = () => {
    setShowSearchSection(!showSearchSection);
    setError(''); // Clear errors when toggling
  };

  const selectedCountry = countries.find(c => c.name === country);
  const difficultyData = apiData ? calculateDifficulty(apiData.competition) : { score: 0, level: 'N/A' };

  return (
    <div className={`min-h-screen ${showResults ? 'bg-gray-100 dark:bg-slate-950' : 'bg-white dark:bg-slate-950'}`} style={{
      fontFamily: showResults ? 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' : 'arial, sans-serif'
    }}>
      
      {/* Header */}
      <div className={`flex items-center justify-between px-6 py-4 ${showResults ? 'bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 shadow-sm' : 'border-b border-gray-200 dark:border-slate-700'}`}>
        <div className="flex items-center gap-3">
          {showResults && (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-600">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
          )}
          <div className={`text-2xl ${showResults ? 'font-semibold text-gray-800 dark:text-slate-100' : 'font-normal dark:text-blue-400'}`} style={{ color: showResults ? undefined : '#0066e4' }}>
            Keyword Explorer
          </div>
        </div>
        
        {/* Toggle Search Button - Top Right */}
        {!showSearchSection && (
          <button
            onClick={toggleSearchSection}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 transition-all duration-200 text-sm font-medium"
          >
            <Search className="w-4 h-4" />
            <span>New Search</span>
          </button>
        )}
      </div>

      {/* Main Content */}
      <div className={`flex flex-col items-center px-4 ${showSearchSection ? 'pt-32' : showResults ? 'pt-0' : 'pt-8'}`}>
        
        {/* Search Section - Original Simple Style */}
        {showSearchSection && (
          <>
            {/* Logo/Title Area */}
            <div className="mb-8 text-center">
              <h1 className="text-6xl font-normal mb-4" style={{ fontFamily: 'arial, sans-serif', color: '#0066e4' }}>
                Keyword
              </h1>
            </div>

            {/* Error Alert */}
            <div className="w-full max-w-4xl">
              <ErrorAlert message={error} onClose={() => setError('')} />
            </div>

            {/* Search Box */}
            <div className="w-full max-w-4xl mb-8">
              <div className="relative">
                <div className={`flex items-center border rounded-full hover:shadow-md transition-shadow duration-200 bg-white dark:bg-slate-800 ${error ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-slate-600'}`} style={{ height: '44px' }}>
                  <Search className="absolute left-4 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => {
                      setKeyword(e.target.value);
                      if (error) setError(''); // Clear error when user starts typing
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                    className="flex-1 px-12 py-3 text-base text-gray-900 dark:text-slate-100 bg-transparent outline-none"
                    placeholder="Enter a keyword..."
                    autoComplete="off"
                  />
                  
                  {/* Country Selector */}
                  <div className="relative mr-2">
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-md transition-colors duration-200"
                    >
                      {selectedCountry && <FlagImage countryCode={selectedCountry.displayCode} size={16} />}
                      <span>{selectedCountry?.displayCode}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute top-full right-0 z-50 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg min-w-48 max-h-60 overflow-y-auto">
                        {countries.map((countryOption) => (
                          <button
                            key={countryOption.code}
                            type="button"
                            onClick={() => {
                              setCountry(countryOption.name);
                              setIsDropdownOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors duration-200 text-left text-sm"
                          >
                            <FlagImage countryCode={countryOption.displayCode} size={16} />
                            <span className="text-gray-700 dark:text-slate-100">{countryOption.name}</span>
                            {country === countryOption.name && (
                              <CheckCircle className="w-4 h-4 ml-auto" style={{ color: '#0066e4' }} />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Search Button */}
              <div className="flex justify-center mt-8 gap-4">
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="px-6 py-2 text-sm text-white rounded hover:opacity-90 transition-all duration-200 disabled:opacity-50"
                  style={{ backgroundColor: '#0066e4' }}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Searching...
                    </div>
                  ) : (
                    'Keyword Search'
                  )}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Results - Dashboard Style */}
        {showResults && apiData && (
          <div className="w-full p-4">
            <div className="space-y-4">

              {/* Main Keyword Result */}
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
                <div className="p-6 border-b border-gray-200 dark:border-slate-700">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h2 className="text-2xl font-semibold mb-2 text-blue-600 dark:text-blue-400">
                        {keyword}
                      </h2>
                      <div className="flex items-center gap-4 text-gray-600 dark:text-slate-400">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span className="uppercase">{apiData.database_country || selectedCountry?.name}</span>
                        </div>
                        <div className="w-1 h-1 bg-gray-400 dark:bg-slate-600 rounded-full"></div>
                        <div>Keyword Analysis Report</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Enhanced Stats Grid */}
                <div className="p-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      {
                        title: 'Monthly Searches',
                        value: formatVolume(apiData.search_volume),
                        icon: Users,
                        subtitle: 'Search Volume',
                        progress: Math.min((safeNumber(apiData.search_volume) || 0) / 1000, 100),
                        color: 'blue'
                      },
                      {
                        title: 'Cost Per Click',
                        value: formatCurrency(apiData.cpc),
                        icon: DollarSign,
                        subtitle: 'Average CPC',
                        progress: Math.min((safeNumber(apiData.cpc) || 0) * 20, 100),
                        color: 'blue'
                      },
                      {
                        title: 'SEO Difficulty',
                        value: `${difficultyData.score}/100`,
                        icon: Target,
                        subtitle: difficultyData.level,
                        progress: difficultyData.score,
                        color: 'blue'
                      },
                      {
                        title: 'Growth Trend',
                        value: formatTrends(apiData.trends),
                        icon: TrendingUp,
                        subtitle: 'Trend Analysis',
                        progress: apiData.trends ? Math.min(parseFloat(apiData.trends.split(',')[0]) * 100, 100) : 0,
                        color: 'blue'
                      }
                    ].map((stat, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-slate-750 transition-all duration-300 hover:scale-105 hover:shadow-sm group"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <stat.icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                          <div className="text-right">
                            <div className="text-xl font-semibold text-gray-800 dark:text-slate-100">{stat.value}</div>
                          </div>
                        </div>

                        <div className="mb-2">
                          <div className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{stat.title}</div>
                          <div className="text-xs text-gray-500 dark:text-slate-400 mb-2">{stat.subtitle}</div>
                          
                          {/* Progress Bar */}
                          <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="absolute top-0 left-0 h-full bg-blue-600 rounded-full transition-all duration-1000 ease-out"
                              style={{ width: `${Math.max(stat.progress, 5)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Related Keywords Section */}
              {apiData.relatedKeywords && apiData.relatedKeywords.length > 0 && (
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
                  <div className="p-6 border-b border-gray-200 dark:border-slate-700">
                    <h3 className="text-xl font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                      <Target className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      Related Keywords
                    </h3>
                    <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">
                      Keywords related to "{keyword}" with search volume and metrics
                    </p>
                  </div>

                  <div className="p-6">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-slate-700">
                            <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-slate-300">Keyword</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-slate-300">Search Volume</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-slate-300">CPC</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-slate-300">Competition</th>
                            <th className="text-center py-3 px-4 font-medium text-gray-700 dark:text-slate-300">Difficulty</th>
                            <th className="text-center py-3 px-4 font-medium text-gray-700 dark:text-slate-300">SERP Check</th>
                          </tr>
                        </thead>
                        <tbody>
                          {apiData.relatedKeywords.slice(0, 10).map((relatedKeyword, index) => {
                            const relatedDifficulty = calculateDifficulty(relatedKeyword.competition);

                            return (
                              <tr key={index} className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
                                <td className="py-3 px-4">
                                  <div className="font-medium text-gray-900 dark:text-slate-100">{relatedKeyword.keyword || 'N/A'}</div>
                                </td>
                                <td className="py-3 px-4 text-right text-gray-700 dark:text-slate-300">
                                  {formatVolume(relatedKeyword.search_volume)}
                                </td>
                                <td className="py-3 px-4 text-right text-gray-700 dark:text-slate-300">
                                  {formatCurrency(relatedKeyword.cpc)}
                                </td>
                                <td className="py-3 px-4 text-right text-gray-700 dark:text-slate-300">
                                  {formatPercentage(relatedKeyword.competition)}
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <div className="py-3 px-4 text-gray-700 dark:text-slate-300">
                                    {relatedDifficulty.score}/100
                                  </div>
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <button
                                    onClick={() => {
                                      // Map KeywordExplorer country codes to SerpChecker codes
                                      const countryCodeMap = { 'uk': 'gb' };
                                      const kwCountry = selectedCountry?.code || 'us';
                                      const serpCountry = countryCodeMap[kwCountry] || kwCountry;
                                      const url = `/SerpChecker?keyword=${encodeURIComponent(relatedKeyword.keyword || '')}&country=${encodeURIComponent(serpCountry)}`;
                                      window.history.pushState({}, '', url);

                                      // Dispatch a popstate event to trigger route change
                                      window.dispatchEvent(new PopStateEvent('popstate'));
                                    }}
                                    className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors duration-200 gap-1"
                                  >
                                    <Eye className="w-3 h-3" />
                                    Check SERP
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {apiData.relatedKeywords.length > 10 && (
                      <div className="mt-4 text-center">
                        <p className="text-sm text-gray-500 dark:text-slate-400">
                          Showing 10 of {apiData.relatedKeywords.length} related keywords
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KeywordExplorer;